/**
 * @file        src/lib/format.js
 * @description Currency / number / date formatters. Single source so we
 *              never sprinkle toFixed/toLocaleString through views.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-06-22 Claude — Added `inr()` short alias matching the booking
 *                          design helper (was: const inr = n => ...).
 *   - 2026-07-03 Claude — Money shows 2 decimals only when the value actually has
 *                          a fractional part (₹2,471.50); whole amounts stay clean
 *                          (₹3,197, not ₹3,197.00).
 */
// Money values show 2-decimal precision ONLY when the value actually carries a
// fractional part from the API (e.g. ₹2,471.50). Whole amounts stay clean —
// ₹3,197, not ₹3,197.00. Single source so views never re-introduce their own
// rounding; change the rule here, not in the views.
function inrDigits(value) {
  const n = Number(value) || 0;
  const hasDecimals = !Number.isInteger(n);
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export function formatINR(value) {
  return `₹${inrDigits(value)}`;
}

/** Design's inline helper: inr(10050) → "₹10,050"; inr(2471.5) → "₹2,471.50". */
export const inr = (n) => `₹${inrDigits(n)}`;

/**
 * ALWAYS-2-decimal money formatter for documents where every amount must line up
 * to the paise — vouchers / e-tickets / receipts. inr2(2523) → "₹2,523.00";
 * inr2(2471.5) → "₹2,471.50". Unlike inr(), it never drops the decimals on a
 * whole-rupee value, so an itemised table and its total read consistently.
 */
export const inr2 = (n) =>
  '₹' + (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatINRCompact(value) {
  const n = Number(value || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return formatINR(n);
}

const fullDate = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
});
const fullDateTime = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
});

export function formatDate(value) {
  if (!value) return '—';
  return fullDate.format(new Date(value));
}
export function formatDateTime(value) {
  if (!value) return '—';
  return fullDateTime.format(new Date(value));
}
